import React, { useState, useRef, useEffect } from 'react';
import { API_BASE } from '../config';

const SUGGESTED_QUESTIONS = [
  "Who is Bernard?",
  "What projects has Bernard built?",
  "What are Bernard's skills?",
  "What is Bernard's work experience?",
  "How can I contact Bernard?",
  "What is Bernard's education?",
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, sender: 'bot', text: "Hi there! I'm Bernard's portfolio assistant. Select a question below to learn more about Bernard." }
  ]);
  const [portfolio, setPortfolio] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/portfolio`);
        const data = await res.json();
        setPortfolio(data);
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
      }
    };
    fetchPortfolio();
  }, []);

  const getReply = (userMessage) => {
    const lower = userMessage.toLowerCase();
    const about = portfolio?.about || {};
    const projects = portfolio?.projects || [];
    const skills = portfolio?.skills || [];
    const experiences = portfolio?.experience || [];

    if (lower.includes('hello') || lower.includes('hi') || lower.includes('hey')) {
      return `Hello! I'm ${about.name || "Bernard"}'s assistant. Ask me about his projects, skills, experience, or how to contact him.`;
    }

    if (lower.includes('who') || lower.includes('about you') || lower.includes('tell me about') || lower.includes('about him')) {
      const name = about.name || 'Bernard Banda';
      const title = about.title || 'Computer Science Student';
      const bio = about.bio || 'Passionate about building impactful applications.';
      return `${name} is a ${title}. ${bio}`;
    }

    if (lower.includes('name')) {
      return `His name is ${about.name || 'Bernard Banda'}.`;
    }

    if (lower.includes('contact') || lower.includes('email') || lower.includes('phone') || lower.includes('reach') || lower.includes('location') || lower.includes('live')) {
      const email = about.email || process.env.REACT_APP_EMAIL || '';
      const phone = process.env.REACT_APP_PHONE || '';
      const location = about.location || 'Lusaka, Zambia';
      return `You can reach Bernard at ${email}${phone ? ` or ${phone}` : ''}. He is based in ${location}. You can also use the Contact page to send him a message directly.`;
    }

    if (lower.includes('skill') || lower.includes('technology') || lower.includes('tech stack') || lower.includes('know')) {
      if (skills.length === 0) {
        return 'Bernard specializes in JavaScript, Python, React, Node.js, PostgreSQL, Git & GitHub, and Docker.';
      }
      const skillList = skills.map(s => s.name).join(', ');
      return `Bernard's skills include: ${skillList}.`;
    }

    if (lower.includes('experience') || lower.includes('work') || lower.includes('job') || lower.includes('career')) {
      if (experiences.length === 0) {
        return 'Bernard has experience as a Full Stack Developer and Backend Developer at Africa University.';
      }
      const expText = experiences.map(exp => `${exp.position} at ${exp.company}`).join('; ');
      return `Experience: ${expText}.`;
    }

    if (lower.includes('project') || lower.includes('built') || lower.includes('portfolio') || lower.includes('app') || lower.includes('work')) {
      if (projects.length === 0) {
        return "Bernard has worked on projects like Smart Agrotrack, Inventory Management App, and AU Transcript Clone.";
      }
      const projectNames = projects.slice(0, 3).map(p => p.title).join(', ');
      return `Some of Bernard's projects include: ${projectNames}. Check the Projects page for details.`;
    }

    if (lower.includes('education') || lower.includes('study') || lower.includes('university') || lower.includes('school') || lower.includes('college')) {
      return 'Bernard is studying Computer Science at Africa University. He also holds a Trades Certificate from Zambia Institute of Mass Communications (ZAMCOM).';
    }

    return "I'm not sure I understood that. Try selecting one of the suggested questions below.";
  };

  const handleQuestionSelect = (question) => {
    const userMsg = { id: Date.now(), sender: 'user', text: question };
    const reply = getReply(question);
    setMessages(prev => [
      ...prev,
      userMsg,
      { id: Date.now() + 1, sender: 'bot', text: reply },
    ]);
  };

  const resetChat = () => {
    setMessages([
      { id: 1, sender: 'bot', text: "Hi there! I'm Bernard's portfolio assistant. Select a question below to learn more about Bernard." }
    ]);
  };

  return (
    <>
      <div style={{ position: 'fixed', bottom: '24px', right: '24px', display: 'flex', gap: '12px', alignItems: 'center', zIndex: 10000 }}>
        <button
          className="chat-toggle-button"
          data-tour="chat-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close chat' : 'Open chat'}
          style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--gradient-primary)', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer', boxShadow: '0 8px 24px rgba(0, 173, 181, 0.4), var(--shadow-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'var(--transition)' }}
        >
          {isOpen ? '×' : '💬'}
        </button>
      </div>
      {isOpen && (
        <div className="chat-bot-container" style={{ position: 'fixed', bottom: '90px', right: '24px', zIndex: 10001, width: '380px', height: '560px', maxHeight: 'calc(100vh - 160px)', backgroundColor: 'white', display: 'flex', flexDirection: 'column', borderRadius: '20px', overflow: 'hidden', border: '2px solid var(--primary)', boxShadow: '0 25px 70px rgba(0, 173, 181, 0.35), 0 10px 30px rgba(0, 0, 0, 0.2)' }}>
          <div className="chat-header" style={{ background: 'var(--gradient-primary)', color: 'white', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: '600', fontSize: '1.1rem' }}>
            <span>Portfolio Assistant</span>
            <div className="chat-header-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={resetChat} aria-label="Reset chat" title="Reset chat" className="chat-reset-button" style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.25rem', cursor: 'pointer' }}>↻</button>
              <button onClick={() => setIsOpen(false)} aria-label="Close chat" style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer', lineHeight: '1' }}>×</button>
            </div>
          </div>
          <div className="chat-messages" style={{ flex: 1, padding: '1rem', overflowY: 'auto', background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {messages.map(msg => (
              <div key={msg.id} className={`chat-message ${msg.sender}`} style={msg.sender === 'user' ? { alignSelf: 'flex-end', background: 'var(--primary)', color: 'white', borderBottomRightRadius: '4px', maxWidth: '80%', padding: '0.75rem 1rem', borderRadius: '12px', wordBreak: 'break-word' } : { alignSelf: 'flex-start', background: 'white', color: 'var(--text-dark)', border: '1px solid #e0e0e0', borderBottomLeftRadius: '4px', maxWidth: '80%', padding: '0.75rem 1rem', borderRadius: '12px', wordBreak: 'break-word' }}>
                {msg.text}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="suggested-questions" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1rem 0.75rem 0.75rem', borderTop: '1px solid #e0e0e0', background: 'white' }}>
            {SUGGESTED_QUESTIONS.map((q, idx) => (
              <button
                key={idx}
                className="suggested-question-btn"
                onClick={() => handleQuestionSelect(q)}
                style={{ background: 'var(--bg-light)', border: '1px solid #e0e0e0', borderRadius: '12px', color: 'var(--text-dark)', cursor: 'pointer', fontSize: '0.85rem', padding: '0.6rem 0.85rem', textAlign: 'left', transition: 'var(--transition)' }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
